// src/services/firebase.js
import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import {
  getAuth, RecaptchaVerifier, signInWithPhoneNumber, PhoneAuthProvider,
  signInWithCredential, onAuthStateChanged,
} from 'firebase/auth';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { getFirestore, doc, getDoc, setDoc, updateDoc, runTransaction, onSnapshot, collection, query, where, orderBy, serverTimestamp, addDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getDatabase, ref, set, update, remove } from 'firebase/database';
import { FEATURES } from '../config/features';
import { registerPlugin, Capacitor } from '@capacitor/core';
import { isNativeApp, useNativePhoneAuth } from '../utils/platform';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';

/** Native bridge only — register once on native; web uses package export (no double-register). */
let firebaseAuthNativePlugin = null;
const getNativeAuthPlugin = () => {
  if (!firebaseAuthNativePlugin) {
    firebaseAuthNativePlugin = registerPlugin('FirebaseAuthentication');
  }
  return firebaseAuthNativePlugin;
};

const getPhoneAuthPlugin = () => (
  useNativePhoneAuth() ? getNativeAuthPlugin() : FirebaseAuthentication
);

const nativePhoneAuthCall = async (method, options) => {
  if (useNativePhoneAuth() && typeof Capacitor.nativePromise === 'function') {
    return Capacitor.nativePromise('FirebaseAuthentication', method, options);
  }
  return getPhoneAuthPlugin()[method](options);
};

const FCM_VAPID_KEY = process.env.REACT_APP_FCM_VAPID_KEY
  || 'BFE1Pa4va663rmeBebHMlB_UUU1R3t-TkUAMq2I6jjxarW93PQpvCiVXeG55u606vzKnj7qMaJHse7gvG48YUgo';

const onSnap = (q, onData, onError) => onSnapshot(
  q,
  onData,
  (err) => {
    console.warn('Firestore listener:', err?.code, err?.message);
    onError?.(err);
  },
);

// ─── ENV VALIDATION ──────────────────────────
const REQUIRED_ENV = [
  'REACT_APP_FIREBASE_API_KEY', 'REACT_APP_FIREBASE_AUTH_DOMAIN',
  'REACT_APP_FIREBASE_PROJECT_ID', 'REACT_APP_FIREBASE_APP_ID',
  'REACT_APP_FIREBASE_DATABASE_URL',
];
const missingEnv = REQUIRED_ENV.filter(k => !process.env[k]);
if (missingEnv.length) console.error(`⚠️ Missing env vars: ${missingEnv.join(', ')}. Check your .env file.`);

// Same Firebase project as customer app
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
};

const app = initializeApp(firebaseConfig);

// App Check — opt-in only (REACT_APP_ENABLE_APP_CHECK=true)
const appCheckEnabled = process.env.REACT_APP_ENABLE_APP_CHECK === 'true';
const recaptchaKey = process.env.REACT_APP_RECAPTCHA_ENTERPRISE_KEY;
if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_APPCHECK_DEBUG_TOKEN) {
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.REACT_APP_APPCHECK_DEBUG_TOKEN;
}
if (appCheckEnabled && recaptchaKey) {
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(recaptchaKey),
    isTokenAutoRefreshEnabled: true,
  });
} else if (appCheckEnabled && !recaptchaKey) {
  console.warn('REACT_APP_ENABLE_APP_CHECK is true but REACT_APP_RECAPTCHA_ENTERPRISE_KEY is missing.');
}

export const auth = getAuth(app);
export const db = getFirestore(app);
export const rtdb = getDatabase(app);

const functionsRegion = process.env.REACT_APP_FUNCTIONS_REGION || 'asia-south1';
const functions = getFunctions(app, functionsRegion);
const listAvailableDeliveriesFn = httpsCallable(functions, 'listAvailableDeliveries');

// ─── NATIVE PHONE AUTH (Capacitor APK) ────────
let nativeVerificationId = null;
let nativePhoneError = null;
let nativePhoneListenersReady = false;
let nativeOtpAttempt = 0;
let nativeOtpCallbacks = { onAutoVerified: null, onCodeReceived: null };

const waitForAuthUser = (maxMs = 4000) => new Promise((resolve) => {
  if (auth.currentUser) {
    resolve(auth.currentUser);
    return;
  }
  const timeout = setTimeout(() => {
    unsub();
    resolve(null);
  }, maxMs);
  const unsub = onAuthStateChanged(auth, (user) => {
    if (user) {
      clearTimeout(timeout);
      unsub();
      resolve(user);
    }
  });
});

const signInWithNativeCredential = async (verificationId, code) => {
  const credential = PhoneAuthProvider.credential(verificationId, code);
  const result = await signInWithCredential(auth, credential);
  nativeVerificationId = null;
  return result.user;
};

export const setNativeOtpCallbacks = (callbacks = {}) => {
  nativeOtpCallbacks = { ...nativeOtpCallbacks, ...callbacks };
};

const waitForNativeVerificationId = (attemptId) => new Promise((resolve, reject) => {
  const started = Date.now();
  const poll = setInterval(() => {
    if (attemptId !== nativeOtpAttempt) {
      clearInterval(poll);
      reject(Object.assign(new Error('OTP request superseded'), { code: 'auth/superseded' }));
      return;
    }
    if (auth.currentUser) {
      clearInterval(poll);
      resolve({ autoVerified: true });
    } else if (nativeVerificationId) {
      clearInterval(poll);
      resolve({ verificationId: nativeVerificationId });
    } else if (nativePhoneError) {
      clearInterval(poll);
      const err = nativePhoneError;
      nativePhoneError = null;
      reject(err);
    } else if (Date.now() - started > 90000) {
      clearInterval(poll);
      reject(Object.assign(new Error('OTP request timed out'), { code: 'auth/timeout' }));
    }
  }, 150);
});

const ensureNativePhoneListeners = async () => {
  if (nativePhoneListenersReady || !useNativePhoneAuth()) return;
  await Promise.all([
    getPhoneAuthPlugin().addListener('phoneCodeSent', (event) => {
      nativeVerificationId = event.verificationId;
    }),
    getPhoneAuthPlugin().addListener('phoneVerificationCompleted', async (event) => {
      if (event.verificationCode && nativeVerificationId) {
        try {
          await signInWithNativeCredential(nativeVerificationId, event.verificationCode);
          nativeOtpCallbacks.onCodeReceived?.(event.verificationCode);
        } catch (e) {
          nativePhoneError = e;
        }
        return;
      }
      if (event.verificationCode) {
        nativeOtpCallbacks.onCodeReceived?.(event.verificationCode);
        return;
      }
      const user = await waitForAuthUser();
      if (user) {
        nativeVerificationId = null;
        nativeOtpCallbacks.onAutoVerified?.();
        return;
      }
      nativePhoneError = Object.assign(new Error('Phone auto-verification failed'), {
        code: 'auth/auto-verify-failed',
      });
    }),
    getPhoneAuthPlugin().addListener('phoneVerificationFailed', (event) => {
      const raw = event?.message || event?.code || 'Phone verification failed';
      nativePhoneError = Object.assign(new Error(raw), {
        code: event?.code || raw,
      });
    }),
  ]);
  nativePhoneListenersReady = true;
};

/** Call once on app start so listeners are ready before login */
export const initNativePhoneAuth = () => {
  if (useNativePhoneAuth()) {
    ensureNativePhoneListeners().catch((e) => console.warn('Native phone auth init:', e?.message));
  }
};

const AUTH_ERROR_KEYS = {
  'auth/invalid-phone-number': 'invalidPhone',
  'auth/too-many-requests': 'tooManyRequests',
  'auth/captcha-check-failed': 'captchaFailed',
  'auth/argument-error': 'captchaFailed',
  'auth/invalid-verification-code': 'invalidOtp',
  'auth/code-expired': 'codeExpired',
  'auth/missing-verification-code': 'missingOtp',
  'auth/quota-exceeded': 'quotaExceeded',
  'auth/app-not-authorized': 'appNotAuthorized',
  'auth/network-request-failed': 'networkFailed',
  'auth/missing-otp-session': 'noOtpSession',
  'auth/referer-blocked': 'refererBlocked',
  'auth/recaptcha-script-failed': 'networkFailed',
};

const NATIVE_AUTH_HINTS = [
  'invalid-cert-hash', 'invalid cert', 'not authorized', 'app identifier',
  'missing a valid app', '17499', '17093', '12500', 'app-not-authorized',
];

export const getAuthErrorKey = (error) => {
  const code = error?.code || '';
  const msg = (error?.message || '').toLowerCase();
  const combined = `${code} ${msg}`.toLowerCase();
  if (AUTH_ERROR_KEYS[code]) return AUTH_ERROR_KEYS[code];
  if (NATIVE_AUTH_HINTS.some((hint) => combined.includes(hint))) {
    return 'appNotAuthorized';
  }
  if (combined.includes('timeout')) return 'timeout';
  if (combined.includes('requests-from-referer') || combined.includes('referer')) {
    return 'refererBlocked';
  }
  if (combined.includes('network')) return 'networkFailed';
  if (combined.includes('too-many') || combined.includes('too many')) return 'tooManyRequests';
  if (combined.includes('quota')) return 'quotaExceeded';
  if (msg.includes('recaptcha')) return 'refererBlocked';
  if (msg.includes('no otp session')) return 'noOtpSession';
  return 'sendFailed';
};

// ─── AUTH ─────────────────────────────────────
const ensureRecaptchaContainer = () => {
  document.querySelectorAll('[id^="recaptcha-container"]').forEach((el) => el.remove());
  const el = document.createElement('div');
  el.id = `recaptcha-container-${Date.now()}`;
  el.setAttribute('aria-hidden', 'true');
  el.style.cssText = 'position:fixed;left:-9999px;width:1px;height:1px;overflow:hidden;pointer-events:none';
  document.body.appendChild(el);
  return el.id;
};

const loadRecaptchaScript = () => {
  if (typeof window !== 'undefined' && window.grecaptcha) return Promise.resolve();
  if (window.recaptchaScriptPromise) return window.recaptchaScriptPromise;
  window.recaptchaScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-mytownly-recaptcha]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('reCAPTCHA script failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.dataset.mytownlyRecaptcha = '1';
    script.onload = () => resolve();
    script.onerror = () => {
      window.recaptchaScriptPromise = null;
      reject(Object.assign(new Error('Could not load reCAPTCHA. Check your connection.'), {
        code: 'auth/recaptcha-script-failed',
      }));
    };
    document.head.appendChild(script);
  });
  return window.recaptchaScriptPromise;
};

const destroyRecaptchaWidget = async () => {
  if (window.recaptchaVerifier) {
    try {
      await window.recaptchaVerifier.clear();
    } catch {
      /* already cleared */
    }
    window.recaptchaVerifier = null;
  }
  window.recaptchaWidgetId = null;
  document.querySelectorAll('[id^="recaptcha-container"]').forEach((el) => el.remove());
};

const getRecaptchaVerifier = async (forceNew = false) => {
  if (useNativePhoneAuth()) {
    throw Object.assign(new Error('reCAPTCHA not used in native app'), { code: 'auth/native-no-recaptcha' });
  }
  if (forceNew || window.recaptchaVerifier) {
    await destroyRecaptchaWidget();
  }
  await loadRecaptchaScript();
  const containerId = ensureRecaptchaContainer();
  window.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
    size: 'invisible',
    callback: () => { },
    'expired-callback': () => {
      window.recaptchaVerifier = null;
      window.confirmationResult = null;
    },
  });
  try {
    window.recaptchaWidgetId = await window.recaptchaVerifier.render();
  } catch (e) {
    await destroyRecaptchaWidget();
    const msg = (e?.message || '').toLowerCase();
    if (msg.includes('network') || msg.includes('failed') || msg.includes('recaptchaparams')) {
      throw Object.assign(new Error(
        'Phone sign-in blocked by API key. Add https://rider.mytownly.in/* to HTTP referrers in Google Cloud Console.',
      ), { code: 'auth/referer-blocked' });
    }
    throw e;
  }
  return window.recaptchaVerifier;
};

/** Preload invisible reCAPTCHA while user enters phone — saves 1–3s on Send OTP. */
export const warmRecaptcha = () => {
  if (useNativePhoneAuth()) return Promise.resolve();
  if (window.recaptchaWarmPromise) return window.recaptchaWarmPromise;
  window.recaptchaWarmPromise = getRecaptchaVerifier()
    .catch(() => { window.recaptchaWarmPromise = null; });
  return window.recaptchaWarmPromise;
};

export const sendOtp = async (phoneNumber) => {
  window.confirmationResult = null;
  if (useNativePhoneAuth()) {
    await ensureNativePhoneListeners();
    nativeOtpAttempt += 1;
    const attemptId = nativeOtpAttempt;
    nativeVerificationId = null;
    nativePhoneError = null;
    try {
      await nativePhoneAuthCall('signInWithPhoneNumber', {
        phoneNumber,
        timeout: 60,
      });
      const waitResult = await waitForNativeVerificationId(attemptId);
      if (waitResult.autoVerified) return { autoVerified: true };
      return { native: true };
    } catch (e) {
      console.error('Native OTP failed:', e);
      throw Object.assign(new Error(e.message || 'Native OTP failed'), {
        code: e.code || 'auth/native-otp-failed',
      });
    }
  }
  const verifier = await (window.recaptchaWarmPromise || getRecaptchaVerifier());
  window.recaptchaWarmPromise = null;
  try {
    const confirmation = await signInWithPhoneNumber(auth, phoneNumber, verifier);
    window.confirmationResult = confirmation;
    return confirmation;
  } catch (e) {
    await destroyRecaptchaWidget();
    const msg = (e?.message || '').toLowerCase();
    if (e?.code === 'auth/network-request-failed' || msg.includes('referer') || msg.includes('requests-from-referer')) {
      throw Object.assign(new Error(
        'Phone sign-in blocked by API key. Add https://rider.mytownly.in/* to HTTP referrers in Google Cloud Console.',
      ), { code: 'auth/referer-blocked' });
    }
    throw e;
  }
};

export const verifyOtp = async (otp) => {
  if (auth.currentUser && !nativeVerificationId && !window.confirmationResult) {
    return auth.currentUser;
  }
  if (useNativePhoneAuth() && nativeVerificationId) {
    const verificationId = nativeVerificationId;
    nativeVerificationId = null;
    try {
      await nativePhoneAuthCall('confirmVerificationCode', {
        verificationId,
        verificationCode: otp,
      });
      const synced = await waitForAuthUser();
      if (synced) return synced;
    } catch (pluginErr) {
      console.warn('Native confirmVerificationCode:', pluginErr?.message);
    }
    return signInWithNativeCredential(verificationId, otp);
  }
  if (!window.confirmationResult) {
    throw Object.assign(new Error('No OTP session. Please request a new OTP.'), {
      code: 'auth/missing-otp-session',
    });
  }
  try {
    const result = await window.confirmationResult.confirm(otp);
    window.confirmationResult = null;
    return result.user;
  } catch (e) {
    if (e?.code === 'auth/invalid-verification-code' || e?.code === 'auth/code-expired') {
      window.confirmationResult = null;
    }
    throw e;
  }
};

// ─── RIDER PROFILE ────────────────────────────
export const getRiderProfile = async (uid) => {
  const snap = await getDoc(doc(db, 'riders', uid));
  return snap.exists() ? snap.data() : null;
};

export const listenToRiderProfile = (uid, callback, onError) => {
  return onSnapshot(
    doc(db, 'riders', uid),
    (snap) => callback(snap.exists() ? snap.data() : null),
    (err) => {
      console.warn('Rider profile listener:', err?.message);
      onError?.(err);
    },
  );
};

export const createOrUpdateRider = async (uid, data) => {
  await setDoc(doc(db, 'riders', uid), { ...data, updatedAt: serverTimestamp() }, { merge: true });
};

// ─── RIDER LOCATION (Realtime) ────────────────
export const updateRiderLocation = async (uid, lat, lng) => {
  const locationRef = ref(rtdb, `driverLocations/${uid}`);
  try {
    await set(locationRef, { lat, lng, updatedAt: Date.now(), isOnline: true });
  } catch (e) {
    console.warn('RTDB location:', e?.message);
  }
};

export const clearRiderLocation = async (uid) => {
  if (!uid) return;
  const locationRef = ref(rtdb, `driverLocations/${uid}`);
  try {
    await remove(locationRef);
  } catch (e) {
    console.warn('RTDB location clear:', e?.message);
  }
};

// ─── RIDE REQUESTS (listen for new rides nearby) ─
export const listenToRideRequests = (callback) => {
  // Listen to Firestore rides collection directly
  const q = query(
    collection(db, 'rides'),
    where('status', '==', 'searching')
  );
  return onSnap(q, (snap) => {
    const requests = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(requests);
  });
};

// ─── ACCEPT RIDE ──────────────────────────────
export const acceptRide = async (rideId, riderData) => {
  // Update Firestore
  await updateDoc(doc(db, 'rides', rideId), {
    status: 'driver_assigned',
    driverId: riderData.uid,
    driverName: riderData.name,
    driverPhone: riderData.phone,
    driverVehicle: riderData.vehicle,
    driverNumber: riderData.vehicleNumber,
    assignedAt: serverTimestamp(),
  });
  // Update Realtime DB
  await update(ref(rtdb, `rideRequests/${rideId}`), {
    status: 'driver_assigned',
    driverId: riderData.uid,
  });
};

// ─── LISTEN TO ASSIGNED RIDE ──────────────────
export const listenToMyRide = (rideId, callback) => {
  return onSnap(doc(db, 'rides', rideId), (snap) => {
    if (snap.exists()) callback({ id: snap.id, ...snap.data() });
  });
};

// ─── UPDATE RIDE STATUS ───────────────────────
export const updateRideStatus = async (rideId, status, extra = {}) => {
  await updateDoc(doc(db, 'rides', rideId), {
    status,
    ...extra,
    updatedAt: serverTimestamp(),
  });
  await update(ref(rtdb, `rideRequests/${rideId}`), { status });
};

// ─── RIDER JOB STATS (deliveries completed — no pay in app) ──────
export const getRiderJobStats = (uid, callback) => {
  let rides = [];
  let orders = [];

  const emit = () => {
    const today = new Date().toDateString();
    const allItems = [
      ...(FEATURES.RIDES_ENABLED ? rides.map(r => ({ ...r, type: 'ride' })) : []),
      ...orders.map(o => ({ ...o, type: 'delivery' })),
    ].sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

    const todayItems = allItems.filter(r => {
      const d = r.updatedAt?.toDate?.() || r.completedAt?.toDate?.();
      return d?.toDateString() === today;
    });

    callback({
      jobs: allItems,
      todayTrips: todayItems.length,
      totalTrips: allItems.length,
    });
  };

  const unsubs = [];
  if (FEATURES.RIDES_ENABLED) {
    unsubs.push(onSnap(
      query(collection(db, 'rides'), where('driverId', '==', uid), where('status', '==', 'completed'), orderBy('createdAt', 'desc')),
      (snap) => { rides = snap.docs.map(d => ({ id: d.id, ...d.data() })); emit(); },
    ));
  }
  unsubs.push(onSnap(
    query(collection(db, 'orders'), where('deliveryPartnerId', '==', uid), where('status', '==', 'delivered'), orderBy('createdAt', 'desc')),
    (snap) => { orders = snap.docs.map(d => ({ id: d.id, ...d.data() })); emit(); },
  ));

  return () => { unsubs.forEach((u) => u()); };
};

// ─── FCM PUSH NOTIFICATIONS ───────────────────
export const registerMessagingSw = async () => {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    await navigator.serviceWorker.ready;
    return reg;
  } catch (e) {
    console.log('FCM SW:', e.message);
    return null;
  }
};

export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  return Notification.requestPermission();
};

export const saveFcmToken = async (uid) => {
  try {
    if (!(await isSupported())) return false;
    const perm = await requestNotificationPermission();
    if (perm !== 'granted') return false;

    const reg = await registerMessagingSw();
    if (!reg) return false;

    const m = getMessaging(app);
    const token = await getToken(m, {
      vapidKey: FCM_VAPID_KEY,
      serviceWorkerRegistration: reg,
    });
    if (token) {
      await updateDoc(doc(db, 'riders', uid), { fcmToken: token });
      return true;
    }
  } catch (e) {
    console.log('FCM:', e.message);
  }
  return false;
};

export const listenToForegroundMessages = async (callback) => {
  if (!(await isSupported())) return () => {};
  const m = getMessaging(app);
  return onMessage(m, callback);
};

export const updateRiderOnlineStatus = async (uid, isOnline, workType = 'rides') => {
  await updateDoc(doc(db, 'riders', uid), { isOnline, workType });
};

// Only approved riders should call this (Dashboard guards before going online)
export const getOrderById = async (orderId) => {
  const snap = await getDoc(doc(db, 'orders', orderId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

const AVAILABLE_POLL_MS = 15_000;

/** Fetch unassigned deliveries via secure callable (no customer PII). */
export const fetchAvailableDeliveries = async ({ riderLat, riderLng } = {}) => {
  const result = await listAvailableDeliveriesFn({ riderLat, riderLng });
  return (result.data?.orders || []).map((o) => ({
    id: o.orderId,
    orderId: o.orderId,
    shopName: o.shopName,
    shopAddress: o.shopAddress,
    itemCount: o.itemCount,
    subtotal: o.subtotal,
    deliveryDistanceKm: o.deliveryDistanceKm,
    riderDistanceKm: o.riderDistanceKm,
    status: o.status,
    paymentMode: o.paymentMode,
    createdAt: o.createdAt,
  }));
};

/** getCoords: () => ({ riderLat?, riderLng? }) — called each poll so GPS stays fresh */
export const listenToDeliveryRequests = (callback, getCoords = () => ({})) => {
  let active = true;
  let pollTimer = null;

  const poll = async () => {
    if (!active) return;
    try {
      const coords = typeof getCoords === 'function' ? getCoords() : getCoords;
      const orders = await fetchAvailableDeliveries(coords || {});
      callback(orders);
    } catch (err) {
      console.warn('Available deliveries poll:', err?.code, err?.message);
    }
    if (active) pollTimer = setTimeout(poll, AVAILABLE_POLL_MS);
  };

  poll();
  return () => {
    active = false;
    if (pollTimer) clearTimeout(pollTimer);
  };
};

export const acceptDeliveryOrder = async (orderId, riderData) => {
  const orderRef = doc(db, 'orders', orderId);
  await runTransaction(db, async (transaction) => {
    const orderSnap = await transaction.get(orderRef);
    if (!orderSnap.exists()) throw new Error('ORDER_NOT_FOUND');
    const order = orderSnap.data();
    if (order.status !== 'confirmed' && order.status !== 'ready') throw new Error('ORDER_NOT_READY');
    if (order.deliveryPartnerId) throw new Error('ORDER_ALREADY_ASSIGNED');
    transaction.update(orderRef, {
      deliveryPartnerId: riderData.uid,
      deliveryPartnerName: riderData.name,
      deliveryPartnerPhone: riderData.phone,
      assignedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });
};

export const listenToMyOrder = (orderId, callback) => {
  return onSnap(doc(db, 'orders', orderId), (snap) => {
    if (snap.exists()) callback({ id: snap.id, ...snap.data() });
  });
};

export const updateOrderStatus = async (orderId, status) => {
  const patch = { status, updatedAt: serverTimestamp() };
  if (status === 'picked_up') patch.pickedUpAt = serverTimestamp();
  if (status === 'delivered') patch.deliveredAt = serverTimestamp();
  await updateDoc(doc(db, 'orders', orderId), patch);
};

const ACTIVE_DELIVERY_STATUSES = ['confirmed', 'ready', 'picked_up'];

/** Rider's in-progress delivery (ready or picked_up) — survives refresh */
export const listenToRiderActiveOrder = (uid, callback) => {
  const q = query(collection(db, 'orders'), where('deliveryPartnerId', '==', uid));
  return onSnap(q, (snap) => {
    const active = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((o) => ACTIVE_DELIVERY_STATUSES.includes(o.status))
      .sort((a, b) => {
        const ta = b.assignedAt?.seconds || b.updatedAt?.seconds || 0;
        const tb = a.assignedAt?.seconds || a.updatedAt?.seconds || 0;
        return ta - tb;
      });
    callback(active[0] || null);
  });
};

/** Average customer riderRating on delivered orders */
export const listenToRiderRating = (uid, callback) => {
  const q = query(
    collection(db, 'orders'),
    where('deliveryPartnerId', '==', uid),
    where('status', '==', 'delivered'),
  );
  return onSnap(q, (snap) => {
    const ratings = snap.docs
      .map((d) => d.data().riderRating)
      .filter((r) => typeof r === 'number' && r >= 1 && r <= 5);
    if (!ratings.length) {
      callback(null);
      return;
    }
    const avg = ratings.reduce((s, r) => s + r, 0) / ratings.length;
    callback(Math.round(avg * 10) / 10);
  });
};

// This function fetches both completed rides AND delivered orders for history

export const getRiderHistory = (uid, callback) => {
  // Listen to completed rides
  const ridesQuery = query(
    collection(db, 'rides'),
    where('driverId', '==', uid),
    where('status', '==', 'completed'),
    orderBy('createdAt', 'desc')
  );

  // Listen to delivered orders
  const ordersQuery = query(
    collection(db, 'orders'),
    where('deliveryPartnerId', '==', uid),
    where('status', '==', 'delivered'),
    orderBy('createdAt', 'desc')
  );

  let rides = [];
  let orders = [];

  const merge = () => {
    const all = [
      ...rides.map(r => ({ ...r, type: 'ride' })),
      ...orders.map(o => ({ ...o, type: 'delivery' })),
    ].sort((a, b) => {
      const dateA = a.completedAt?.toDate?.() || a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
      const dateB = b.completedAt?.toDate?.() || b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
      return dateB - dateA;
    });
    callback(all);
  };

  const unsubRides = FEATURES.RIDES_ENABLED ? onSnap(ridesQuery, (snap) => {
    rides = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    merge();
  }) : () => {};

  const unsubOrders = onSnap(ordersQuery, (snap) => {
    orders = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    merge();
  });

  // Return combined unsubscribe
  return () => { unsubRides(); unsubOrders(); };
};